import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Alert, AlertCondition, AlertAction } from '../types';
import { transcriptionQueueApi } from '../api/transcriptionQueueApi';

export interface AlertsState {
  alerts: Alert[];
  activeAlerts: Alert[];
  alertHistory: Array<{
    alert: Alert;
    triggeredAt: string;
    resolvedAt?: string;
    acknowledged?: boolean;
    acknowledgedBy?: string;
  }>;
  loading: boolean;
  error: string | null;
}

const initialState: AlertsState = {
  alerts: [],
  activeAlerts: [],
  alertHistory: [],
  loading: false,
  error: null,
};

export const fetchAlerts = createAsyncThunk<Alert[]>(
  'transcriptionAlerts/fetchAlerts',
  async () => {
    const response = await transcriptionQueueApi.getAlerts();
    return response;
  }
);

export const createAlert = createAsyncThunk<Alert, Omit<Alert, 'id' | 'created' | 'triggerCount'>>(
  'transcriptionAlerts/createAlert',
  async (alertData) => {
    const response = await transcriptionQueueApi.createAlert(alertData);
    return response;
  }
);

export const updateAlert = createAsyncThunk<Alert, { id: string; data: Partial<Alert> }>(
  'transcriptionAlerts/updateAlert',
  async ({ id, data }) => {
    const response = await transcriptionQueueApi.updateAlert(id, data);
    return response;
  }
);

export const deleteAlert = createAsyncThunk<void, string>(
  'transcriptionAlerts/deleteAlert',
  async (id) => {
    await transcriptionQueueApi.deleteAlert(id);
  }
);

export const acknowledgeAlert = createAsyncThunk<void, string>(
  'transcriptionAlerts/acknowledgeAlert',
  async (alertId) => {
    await transcriptionQueueApi.acknowledgeAlert(alertId);
  }
);

const alertsSlice = createSlice({
  name: 'transcriptionAlerts',
  initialState,
  reducers: {
    addActiveAlert: (state, action: PayloadAction<Alert>) => {
      const exists = state.activeAlerts.some((a) => a.id === action.payload.id);
      if (!exists) {
        state.activeAlerts.push(action.payload);
        state.alertHistory.push({
          alert: action.payload,
          triggeredAt: new Date().toISOString(),
        });
      }
    },
    removeActiveAlert: (state, action: PayloadAction<string>) => {
      state.activeAlerts = state.activeAlerts.filter((a) => a.id !== action.payload);
      const historyItem = state.alertHistory.find(
        (h) => h.alert.id === action.payload && !h.resolvedAt
      );
      if (historyItem) {
        historyItem.resolvedAt = new Date().toISOString();
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.alerts = action.payload;
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch alerts';
      })
      .addCase(createAlert.fulfilled, (state, action) => {
        state.alerts.push(action.payload);
      })
      .addCase(updateAlert.fulfilled, (state, action) => {
        const index = state.alerts.findIndex((a) => a.id === action.payload.id);
        if (index > -1) {
          state.alerts[index] = action.payload;
        }
      })
      .addCase(deleteAlert.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.alerts = state.alerts.filter((a) => a.id !== id);
      })
      .addCase(acknowledgeAlert.fulfilled, (state, action) => {
        const alertId = action.meta.arg;
        const historyItem = state.alertHistory.find(
          (h) => h.alert.id === alertId && !h.acknowledged
        );
        if (historyItem) {
          historyItem.acknowledged = true;
          historyItem.acknowledgedBy = 'current-user';
        }
      });
  },
});

export const { addActiveAlert, removeActiveAlert, clearError } = alertsSlice.actions;

export default alertsSlice.reducer;